import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable stats override dialog.
 *
 * Wraps the existing ``/playhq/admin/players/stats-overrides`` endpoints
 * so any admin surface can offer the same "override every stat" UX that
 * lives on the PlayHQ IDs tab. Fields with an empty value fall back to
 * the live aggregate from ``/playhq/leaderboards``.
 *
 * Props:
 *   open, onOpenChange — controlled
 *   playerKey          — normalized lowercase full name (matches match_stat_lines.player_key)
 *   playerName         — display label
 *   onSaved(overrides) — called with the new override dict after save/clear
 */
export function StatsOverrideDialog({ open, onOpenChange, playerKey, playerName, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasOverride, setHasOverride] = useState(false);

  useEffect(() => {
    if (!open || !playerKey) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [lbRes, ovRes] = await Promise.all([
          api.get("/playhq/leaderboards"),
          api.get("/playhq/admin/players/stats-overrides"),
        ]);
        if (cancelled) return;
        const row = (lbRes.data?.players || []).find((x) => x.player_key === playerKey) || {};
        const overrides = (ovRes.data?.overrides || {})[playerKey] || {};
        const fields = FIELD_KEYS;
        const prefilled = {};
        for (const f of fields) {
          prefilled[f] = overrides[f] !== undefined && overrides[f] !== null ? String(overrides[f]) : "";
          prefilled[`__computed_${f}`] = row?.[f] != null ? String(row[f]) : "—";
        }
        setDraft(prefilled);
        setHasOverride(Object.keys(overrides).length > 0);
      } catch (err) {
        toast.error("Couldn't load current stats");
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, playerKey, onOpenChange]);

  const save = async () => {
    if (!playerKey) return;
    setSaving(true);
    try {
      const cleaned = {};
      Object.entries(draft).forEach(([k, v]) => {
        if (k.startsWith("__computed_")) return;
        if (v === "" || v == null) return;
        cleaned[k] = v;
      });
      const res = await api.put("/playhq/admin/players/stats-overrides", {
        player_key: playerKey,
        player_name: playerName,
        overrides: cleaned,
      });
      const saved = res.data?.overrides || {};
      onSaved?.(saved);
      toast.success(
        Object.keys(saved).length === 0
          ? `Override cleared · ${playerName}`
          : `Override saved · ${playerName}`
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!playerKey) return;
    if (!window.confirm(`Clear override for ${playerName}? Numbers revert to the aggregate.`)) return;
    setSaving(true);
    try {
      await api.delete(`/playhq/admin/players/stats-overrides/${encodeURIComponent(playerKey)}`);
      onSaved?.({});
      toast.success(`Override cleared · ${playerName}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="stats-override-dialog">
        <DialogHeader>
          <DialogTitle>Edit stats · {playerName}</DialogTitle>
          <DialogDescription>
            Any value you type here overrides the aggregate on the career leaderboard. Leave a field blank to keep the raw calculated number.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading current stats…</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <StatsEditGroup title="Batting" fields={[
              ["matches", "Matches"],
              ["innings_batted", "Innings"],
              ["runs", "Runs"],
              ["highest", "Highest score"],
              ["not_outs", "Not outs"],
              ["batting_average", "Average"],
              ["fifties", "50s"],
              ["centuries", "100s"],
              ["ducks", "Ducks"],
            ]} draft={draft} setDraft={setDraft} />

            <StatsEditGroup title="Bowling" fields={[
              ["overs", "Overs (e.g. 42.3)"],
              ["maidens", "Maidens"],
              ["runs_conceded", "Runs conceded"],
              ["wickets", "Wickets"],
              ["bowling_average", "Average"],
              ["economy", "Economy"],
              ["best_bowling", "Best bowling (e.g. 5/12)"],
            ]} draft={draft} setDraft={setDraft} />

            <StatsEditGroup title="Fielding" fields={[
              ["catches", "Catches (total)"],
              ["wk_catches", "Wk catches"],
              ["non_wk_catches", "Non-wk catches"],
              ["stumpings", "Stumpings"],
              ["run_outs", "Run outs"],
            ]} draft={draft} setDraft={setDraft} />
          </div>
        )}
        <DialogFooter className="gap-2">
          {hasOverride && (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-500/10 mr-auto"
              onClick={clear}
              disabled={saving || loading}
              data-testid="stats-override-clear"
            >
              Clear override
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="stats-override-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || loading} data-testid="stats-override-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</> : "Save override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FIELD_KEYS = [
  "matches", "innings_batted", "runs", "batting_average", "highest",
  "not_outs", "fifties", "centuries", "ducks",
  "overs", "wickets", "runs_conceded", "maidens",
  "bowling_average", "economy", "best_bowling",
  "catches", "wk_catches", "non_wk_catches", "stumpings", "run_outs",
];

function StatsEditGroup({ title, fields, draft, setDraft }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-2">{title}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map(([key, label]) => {
          const value = draft[key] ?? "";
          const computed = draft[`__computed_${key}`] ?? "—";
          return (
            <div key={key}>
              <Label className="text-xs mb-1 block" htmlFor={`stats-${key}`}>
                {label}
                <span className="ml-2 text-muted-foreground/70">(aggregate: {computed})</span>
              </Label>
              <Input
                id={`stats-${key}`}
                value={value}
                onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={computed !== "—" ? computed : ""}
                className="h-9 rounded-lg text-sm"
                data-testid={`stats-input-${key}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
